// Guardia mínima para evitar refetches redundantes.
//
// Uso típico dentro de un thunk:
//
//   export function fetchBookingAlerts() {
//     return async (dispatch) => {
//       if (staleGuard("bookings/alerts")) return;
//       // ... fetch real ...
//     };
//   }
//
// Cómo funciona: guardamos en un Map en memoria la última vez que "algo"
// (una clave) se refrescó. Si vuelven a pedir la misma clave antes del
// TTL, devolvemos `true` → el caller sabe que puede saltarse el fetch.
//
// Casos de uso típicos en TakeMyOrder:
//   - Componentes de topbar que remontan al navegar entre mesa y TPV
//     (BookingAlerts, AlertsCenter, ScheduleAlertsBadge, TpvUpcoming...).
//     Su `useEffect` inicial dispatcha el fetch cada vez; con staleGuard
//     los 6-10 GETs redundantes por navegación se colapsan al primero.
//   - Datos ya hidratados desde `loadTpvBootstrap()` — el componente NO
//     sabe que el store ya tiene la data. staleGuard evita el segundo GET
//     mientras dura el TTL.
//
// NO usar para:
//   - Datos por mesa específica (`getCheckout/{id}`, `getMesa/{id}`) —
//     el ID cambia y quieres el dato fresco al entrar en OTRA mesa.
//     Para esos casos usa una clave que incluya el ID
//     (staleGuard(`checkout/${idMesa}`)).
//   - Mutaciones (POST/PUT/DELETE).
//   - Datos operativos en tiempo real (pedidos activos, mesas ocupadas)
//     — para eso está SignalR.

const lastFetchAt = new Map();

// TTL por defecto: 5 min. Suficiente para saltar los remounts naturales
// del navegador (cambio de ruta, tab background→foreground) sin dejar
// datos rancios.
const DEFAULT_TTL_MS = 5 * 60 * 1000;

/**
 * @param {string} key         Clave estable del recurso (ej. "bookings/alerts").
 * @param {number} [ttlMs]     Ventana durante la cual se considera fresco.
 * @returns {boolean}          true si está fresco (skip fetch); false si toca fetch.
 *
 * Efecto lateral: si devuelve false (toca fetch), ADEMÁS marca el timestamp
 * actual — así llamadas paralelas dentro del mismo tick no se disparan
 * ambas. El caller no necesita marcar nada.
 */
export function staleGuard(key, ttlMs = DEFAULT_TTL_MS) {
  const now = Date.now();
  const last = lastFetchAt.get(key);
  if (last && now - last < ttlMs) return true;
  lastFetchAt.set(key, now);
  return false;
}

/**
 * Invalida una clave: fuerza que el siguiente `staleGuard(key)` vuelva a
 * disparar el fetch. Útil tras una mutación exitosa que quiere refrescar
 * (p.ej. crear reserva → invalidateStaleGuard("bookings/upcoming")).
 */
export function invalidateStaleGuard(key) {
  lastFetchAt.delete(key);
}

/**
 * Invalida TODO. Uso en logout / cambio de restaurante.
 */
export function resetStaleGuard() {
  lastFetchAt.clear();
  inflight.clear();
}

// ─────────────────────────────────────────────────────────────────
// dedupeInFlight — dedup de peticiones EN VUELO (no solo por TTL).
//
// Problema que resuelve: cuando N componentes disparan el mismo thunk
// en el mismo tick (mount inicial del dashboard), TODOS ven el state
// flag "loaded=false" porque la primera petición aún no resolvió →
// N GETs al backend. staleGuard(key) solo dedup POST-hoc por
// timestamp; no ayuda cuando las llamadas son simultáneas.
//
// Uso en un thunk:
//
//   export function fetchFoo() {
//     return (dispatch) => dedupeInFlight("foo", async () => {
//       const res = await axios.get("/foo");
//       dispatch(setFoo(res.data));
//       return res.data;
//     });
//   }
//
// La primera llamada crea la promise, la registra en `inflight`, y
// la limpia al resolve/reject. Llamadas subsiguientes con la misma
// key devuelven la misma promise mientras dure el fetch. Combinable
// con staleGuard (dedup por tiempo) — usa dedupeInFlight para el
// tick actual, staleGuard para los siguientes 5min.
// ─────────────────────────────────────────────────────────────────

const inflight = new Map();

/**
 * @param {string} key                     Misma convención que staleGuard.
 * @param {() => Promise<T>} factory       Función que crea la promise real.
 * @returns {Promise<T>}                   La promise (compartida si ya había una).
 */
export function dedupeInFlight(key, factory) {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = Promise.resolve()
    .then(factory)
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
}
